// ============================================================================
// Kısayol Kataloğu — pinlenebilir sayfaların TEK doğruluk kaynağı.
// Kaynak: MenuInner.tsx (etiket + gruplama) + PrivateRoutes.tsx (path + izin).
// Yeni bir sayfa kısayol olarak sunulacaksa BURAYA bir satır eklenir.
//
// Not: `permission` alanı `fetch-permission.ts` ile aynı sözdizimini kullanır;
// "ROLE_A || ROLE_B" => en az birine sahip olmak yeterli. `permission` boş ise
// sayfa tüm oturum açmış kullanıcılara açıktır.
//
// `icon` alanı Metronic duotone SVG yoludur; KTSVG ile render edilir.
//
// Yönetim Paneli (/admin) kartları ayrıca ELLE eklenmez: `admin-menu-items.ts`
// içindeki kart listesi ROLE_ADMIN izniyle otomatik olarak kataloğa katılır
// (bkz. ADMIN_CARD_SHORTCUTS). Statik katalogta zaten aynı path'e sahip bir
// satır varsa o kart tekrar üretilmez.
// ============================================================================

import { ADMIN_GROUP_ORDER, adminMenuItems } from 'src/app/pages/admin/admin-menu-items';

/** Sabit gruplar: Genel, CRM, Raporlar, E-Ticaret, DEP, Yönetim + dinamik 'Yönetim Paneli › <grup>' */
export type ShortcutGroup = string;

export interface ShortcutCatalogItem {
  /** Kalıcı benzersiz kimlik (path'ten bağımsız — backend yalnızca bunu saklar) */
  key: string;
  /** React Router yolu */
  path: string;
  /** Görünen etiket */
  label: string;
  /** Metronic duotone SVG yolu (KTSVG path prop'una verilir) */
  icon: string;
  /** Erişim izni ('||' OR desteklenir). Boş/undefined => herkese açık */
  permission?: string;
  /** Seçici çekmecede gruplama */
  group: ShortcutGroup;
}

const STATIC_SHORTCUT_CATALOG: ShortcutCatalogItem[] = [
  // ---------------------------------------------------------------- Genel
  { key: 'my-hub', path: '/my-hub', label: 'Kişisel Merkezim', icon: '/media/icons/duotone/Home/Home-heart.svg', group: 'Genel' },
  { key: 'dashboard', path: '/dashboard', label: 'Anasayfa', icon: '/media/icons/duotone/Home/Home.svg', group: 'Genel' },
  { key: 'messages', path: '/messages', label: 'Mesajlar', icon: '/media/icons/duotone/Communication/Chat.svg', group: 'Genel' },
  { key: 'documents', path: '/documents', label: 'Medya İçerikleri', icon: '/media/icons/duotone/Files/Group-folders.svg', group: 'Genel' },
  { key: 'todos', path: '/todos', label: 'Notlarım / Görevler', icon: '/media/icons/duotone/Communication/Clipboard-list.svg', permission: 'ROLE_NOTES', group: 'Genel' },
  { key: 'workflow', path: '/workflow', label: 'İş Akışları', icon: '/media/icons/duotone/Design/Component.svg', permission: 'ROLE_WORKFLOWS', group: 'Genel' },

  // ---------------------------------------------------------------- CRM
  { key: 'customers', path: '/customers', label: 'Müşterilerim', icon: '/media/icons/duotone/Communication/Group.svg', permission: 'ROLE_CRM_CUSTOMERS', group: 'CRM' },
  {
    key: 'customer-interactions',
    path: '/customer-interactions',
    label: 'Müşteri Etkileşimleri',
    icon: '/media/icons/duotone/Communication/Group-chat.svg',
    permission: 'ROLE_CRM_CUSTOMERS || ROLE_CRM_CUSTOMER_VISITS || ROLE_CRM_CUSTOMER_FIRST_INVOICE || ROLE_CRM_CUSTOMER_NEWTOMAC',
    group: 'CRM',
  },
  {
    key: 'my-potential-customers',
    path: '/my-potential-customers',
    label: 'Potansiyel Müşteri Takibi',
    icon: '/media/icons/duotone/Communication/Add-user.svg',
    permission: 'ROLE_CRM_CUSTOMERS',
    group: 'CRM',
  },
  {
    key: 'my-dormant-customers',
    path: '/my-dormant-customers',
    label: 'Gerçek Müşteri Takibi',
    icon: '/media/icons/duotone/Home/Timer.svg',
    permission: 'ROLE_CRM_CUSTOMERS',
    group: 'CRM',
  },
  {
    key: 'my-churn-risks',
    path: '/my-churn-risks',
    label: 'Soğuyan Müşteriler',
    icon: '/media/icons/duotone/Weather/Snow1.svg',
    permission: 'ROLE_CRM_CUSTOMERS',
    group: 'CRM',
  },
  { key: 'quotes', path: '/quotes', label: 'Tekliflerim', icon: '/media/icons/duotone/Shopping/Price1.svg', permission: 'ROLE_CRM_QUOTES', group: 'CRM' },
  {
    key: 'mailing',
    path: '/mailing',
    label: 'Mailing Pazarlama',
    icon: '/media/icons/duotone/Communication/Outgoing-box.svg',
    permission: 'ROLE_CRM_MAILING || ROLE_ADMIN',
    group: 'CRM',
  },
  {
    key: 'products-on-the-way',
    path: '/products-on-the-way',
    label: 'Yoldaki Ürünler',
    icon: '/media/icons/duotone/Navigation/Route.svg',
    permission: 'ROLE_CRM_PRODUCT_PRODUCTSONTHEWAY',
    group: 'CRM',
  },
  { key: 'brand-works', path: '/brand-works', label: 'Proje Satışları', icon: '/media/icons/duotone/Design/Layers.svg', permission: 'ROLE_CRM_PRODUCT_BRANDWORKS', group: 'CRM' },
  {
    key: 'pipeline-records',
    path: '/pipeline-records',
    label: 'Pipeline Kayıtları',
    icon: '/media/icons/duotone/Layout/Layout-4-blocks.svg',
    permission: 'ROLE_CRM_PIPELINE',
    group: 'CRM',
  },
  {
    key: 'pipeline-report',
    path: '/pipeline-report',
    label: 'Pipeline Raporu',
    icon: '/media/icons/duotone/Shopping/Chart-line1.svg',
    permission: 'ROLE_CRM_PIPELINE',
    group: 'CRM',
  },
  { key: 'crt-customers', path: '/crt-customers', label: 'Major Müşteriler', icon: '/media/icons/duotone/Home/Building.svg', permission: 'ROLE_CRM_REBATE_MAJORS', group: 'CRM' },
  {
    key: 'crt-discount-rates',
    path: '/crt-discount-rates',
    label: 'Major İndirim Oranları',
    icon: '/media/icons/duotone/Shopping/Sale1.svg',
    permission: 'ROLE_CRM_REBATE_MAJORS_RATES',
    group: 'CRM',
  },
  {
    key: 'rebate-discounts',
    path: '/rebate-discounts',
    label: 'İndirim Oranları',
    icon: '/media/icons/duotone/Shopping/Sale2.svg',
    permission: 'ROLE_CRM_REBATE_DISCOUNTS',
    group: 'CRM',
  },
  {
    key: 'marketing-customers',
    path: '/marketing-customers',
    label: 'Marketing Müşterileri',
    icon: '/media/icons/duotone/Communication/Send.svg',
    permission: 'ROLE_ADMIN',
    group: 'CRM',
  },
  {
    key: 'customer-distribution',
    path: '/customer-distribution',
    label: 'Müşteri Dağıtım Yönetimi',
    icon: '/media/icons/duotone/Navigation/Exchange.svg',
    permission: 'ROLE_ADMIN',
    group: 'CRM',
  },
  {
    key: 'lead-scanning',
    path: '/lead-scanning',
    label: 'Aday Taraması ve AI Sınıflandırma',
    icon: '/media/icons/duotone/General/Search.svg',
    permission: 'ROLE_ADMIN',
    group: 'CRM',
  },
  {
    key: 'lead-scanning-analytics',
    path: '/lead-scanning-analytics',
    label: 'Aday Taraması Analiz Raporu',
    icon: '/media/icons/duotone/Shopping/Chart-bar1.svg',
    permission: 'ROLE_ADMIN',
    group: 'CRM',
  },
  {
    key: 'customer-surveys',
    path: '/customer-surveys',
    label: 'Müşteri Anketleri',
    icon: '/media/icons/duotone/Communication/Clipboard-check.svg',
    permission: 'ROLE_ADMIN',
    group: 'CRM',
  },

  // ---------------------------------------------------------------- Raporlar
  {
    key: 'reports-sales-summary',
    path: '/reports/sales-summary',
    label: 'Satış Özet Raporları',
    icon: '/media/icons/duotone/Shopping/Chart-bar1.svg',
    permission: 'ROLE_REPORT_SALESS',
    group: 'Raporlar',
  },
  {
    key: 'reports-sales-dashboard',
    path: '/reports/sales-dashboard',
    label: 'Satış Raporları (Detaylı)',
    icon: '/media/icons/duotone/Shopping/Chart-line2.svg',
    permission: 'ROLE_REPORT_SALES',
    group: 'Raporlar',
  },
  {
    key: 'reports-purchase-sales-comparison',
    path: '/reports/purchase-sales-comparison',
    label: 'Stok Öneri Raporları',
    icon: '/media/icons/duotone/Shopping/Box3.svg',
    permission: 'ROLE_REPORT_STOCK',
    group: 'Raporlar',
  },
  {
    key: 'reports-stock-movement-analysis',
    path: '/reports/stock-movement-analysis',
    label: 'Durgun Stok Raporu',
    icon: '/media/icons/duotone/Home/Clock.svg',
    permission: 'ROLE_REPORT_AGED_STOCK',
    group: 'Raporlar',
  },
  {
    key: 'reports-dep-customer-analysis',
    path: '/reports/dep-customer-analysis',
    label: 'DEP Müşteri Analizi',
    icon: '/media/icons/duotone/Communication/Group.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-apple-sales-analysis',
    path: '/reports/apple-sales-analysis',
    label: 'Apple Satış Analizi',
    icon: '/media/icons/duotone/General/iphone.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-apple-sector-report',
    path: '/reports/apple-sector-report',
    label: 'Apple Sektör Raporu',
    icon: '/media/icons/duotone/General/iphone.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-apple-new-customers',
    path: '/reports/apple-new-customers',
    label: 'FY Yeni Müşteriler (Apple)',
    icon: '/media/icons/duotone/General/iphone.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-quote-reports',
    path: '/reports/quote-reports',
    label: 'Teklif Raporları',
    icon: '/media/icons/duotone/Shopping/Price2.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-salesman-quarterly-review',
    path: '/reports/salesman-quarterly-review',
    label: 'Çeyreklik Toplantı Raporu',
    icon: '/media/icons/duotone/Interface/Calendar.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-admin-customers',
    path: '/reports/admin-customers',
    label: 'Tüm Müşteriler',
    icon: '/media/icons/duotone/Communication/Adress-book1.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-assigned-customers',
    path: '/reports/assigned-customers-report',
    label: 'Potansiyel Müşteriler',
    icon: '/media/icons/duotone/Communication/Contact1.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-contact-tracking',
    path: '/reports/contact-tracking-report',
    label: 'İletişim Bilgisi Takibi',
    icon: '/media/icons/duotone/Communication/Call.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },
  {
    key: 'reports-mailing',
    path: '/admin/mailing-reports',
    label: 'Mailing Raporları',
    icon: '/media/icons/duotone/Communication/Mail-opened.svg',
    permission: 'ROLE_ADMIN',
    group: 'Raporlar',
  },

  // ---------------------------------------------------------------- E-Ticaret
  {
    key: 'product-comparison',
    path: '/product-comparison',
    label: 'Ürün Karşılaştırma Sistemi',
    icon: '/media/icons/duotone/Shopping/Bag1.svg',
    permission: 'ROLE_ECOMMERCE',
    group: 'E-Ticaret',
  },
  {
    key: 'ticimax-orders',
    path: '/ticimax-orders',
    label: 'Ticimax Siparişleri',
    icon: '/media/icons/duotone/Shopping/Cart1.svg',
    permission: 'ROLE_ECOMMERCE',
    group: 'E-Ticaret',
  },
  {
    key: 'apple-program',
    path: '/apple-program',
    label: 'Sektör Programı',
    icon: '/media/icons/duotone/General/Star.svg',
    permission: 'ROLE_APPLE_PROGRAM',
    group: 'CRM',
  },
  {
    key: 'stock-alarms',
    path: '/stock-alarms',
    label: 'Stok Alarmları',
    icon: '/media/icons/duotone/General/Notifications1.svg',
    permission: 'ROLE_STOCK_ALARM',
    group: 'E-Ticaret',
  },

  // ---------------------------------------------------------------- DEP
  {
    key: 'dep-enrollments',
    path: '/dep/enrollments',
    label: 'DEP Enrollments',
    icon: '/media/icons/duotone/Communication/Clipboard-check.svg',
    permission: 'ROLE_ENROLLMENTS',
    group: 'DEP',
  },
  { key: 'dep-devices', path: '/dep/devices', label: 'DEP Devices', icon: '/media/icons/duotone/Devices/iPhone-X.svg', permission: 'ROLE_DEVICES', group: 'DEP' },
  { key: 'dep-customers', path: '/dep/customers', label: 'DEP Customers', icon: '/media/icons/duotone/Communication/Group.svg', permission: 'ROLE_DEPCUSTOMERS', group: 'DEP' },

  // ---------------------------------------------------------------- Yönetim
  { key: 'admin', path: '/admin', label: 'Yönetim Paneli', icon: '/media/icons/duotone/General/Settings-1.svg', permission: 'ROLE_ADMIN', group: 'Yönetim' },
  {
    key: 'admin-live-activity',
    path: '/admin/live-activity',
    label: 'Canlı Aktivite',
    icon: '/media/icons/duotone/General/Thunder.svg',
    permission: 'ROLE_ADMIN',
    group: 'Yönetim',
  },
  {
    key: 'admin-scheduled-jobs',
    path: '/admin/scheduled-jobs',
    label: 'Zamanlanmış Görevler',
    icon: '/media/icons/duotone/Code/Time-schedule.svg',
    permission: 'ROLE_ADMIN',
    group: 'Yönetim',
  },
  { key: 'admin-products', path: '/admin/products', label: 'Ürün Yönetimi', icon: '/media/icons/duotone/Shopping/Box1.svg', permission: 'ROLE_ADMIN', group: 'Yönetim' },
  { key: 'admin-announcements', path: '/admin/announcements', label: 'Duyurular', icon: '/media/icons/duotone/Communication/Flag.svg', permission: 'ROLE_ADMIN', group: 'Yönetim' },
  {
    key: 'admin-db-backups',
    path: '/admin/db-backups',
    label: 'Veritabanı Yedekleri',
    icon: '/media/icons/duotone/Devices/Server.svg',
    permission: 'ROLE_ADMIN',
    group: 'Yönetim',
  },
  {
    key: 'users',
    path: '/users',
    label: 'Kullanıcılar',
    icon: '/media/icons/duotone/Communication/Shield-user.svg',
    permission: 'ROLE_USERS || ROLE_PERMISSIONS',
    group: 'Yönetim',
  },
  { key: 'branches', path: '/branches', label: 'Şubeler', icon: '/media/icons/duotone/Home/Home2.svg', permission: 'ROLE_BRANCHES', group: 'Yönetim' },
];

// ---------------------------------------------------------------- Yönetim Paneli kartları
/** Kısayol anahtarı ön eki — path'ten türetilir, backend yalnızca bu string'i saklar */
export const ADMIN_CARD_KEY_PREFIX = 'admin-card:';
/** Seçicide admin kartlarının gruplandığı başlık ön eki (alt grup admin sayfasındaki grup adı) */
export const ADMIN_CARD_GROUP_PREFIX = 'Yönetim Paneli › ';

const staticPaths = new Set(STATIC_SHORTCUT_CATALOG.map((i) => i.path));
const seenAdminPaths = new Set<string>();

/** /admin sayfasındaki kartlardan türetilen kısayollar (yalnızca ROLE_ADMIN) */
export const ADMIN_CARD_SHORTCUTS: ShortcutCatalogItem[] = adminMenuItems
  .filter((card) => {
    if (staticPaths.has(card.to) || seenAdminPaths.has(card.to)) return false;
    seenAdminPaths.add(card.to);
    return true;
  })
  .map((card) => ({
    key: `${ADMIN_CARD_KEY_PREFIX}${card.to}`,
    path: card.to,
    label: card.title,
    icon: card.icon,
    permission: 'ROLE_ADMIN',
    group: `${ADMIN_CARD_GROUP_PREFIX}${card.group}`,
  }));

/** Tam katalog: statik sayfalar + yönetim paneli kartları */
export const SHORTCUT_CATALOG: ShortcutCatalogItem[] = [...STATIC_SHORTCUT_CATALOG, ...ADMIN_CARD_SHORTCUTS];

/** Katalogu anahtar -> öğe olarak hızlı erişim için indeksler */
export const SHORTCUT_CATALOG_MAP: Record<string, ShortcutCatalogItem> = SHORTCUT_CATALOG.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {} as Record<string, ShortcutCatalogItem>);

/** Seçici çekmecedeki grup gösterim sırası */
export const SHORTCUT_GROUP_ORDER: ShortcutGroup[] = ['Genel', 'CRM', 'Raporlar', 'E-Ticaret', 'DEP', 'Yönetim', ...ADMIN_GROUP_ORDER.map((g) => `${ADMIN_CARD_GROUP_PREFIX}${g}`)];

/**
 * fetch-permission.ts ile aynı mantık: 'ROLE_A || ROLE_B' => herhangi biri yeterli.
 * Boş/undefined izin => herkese açık.
 */
export function hasShortcutPermission(authKeys: string[] | undefined, permission?: string): boolean {
  if (!permission) return true;
  if (!authKeys) return false;
  if (permission.includes('||')) {
    return permission
      .split('||')
      .map((p) => p.trim())
      .some((p) => authKeys.includes(p));
  }
  return authKeys.includes(permission);
}
